import type {DefinedAction, DefinedActionPerformer} from 'shared/defined-actions';
import type {Auth} from 'src/typings';
import type {CurrentPageDataService} from 'shared/page';
import type {CurrentAuthService} from 'shared/services';
import type {KpMainMenuSettings, MenuItem} from 'shared/components/kp-main-menu/types';
import type {Readable} from 'svelte/store';
import type {Observable} from 'rxjs';
import type LoginService from 'shared/login/login.service';
import {isGoToMenuItemAction} from 'shared/defined-actions/utils';
import {exists} from 'shared/utils/custom-utils';
import {writable} from 'svelte/store';

declare global {
    interface Window {
        mainMenu: KpMainMenuSettings
    }
}

export class KpMainMenuService {
    public static serviceName = 'kpMainMenuService';

    private mainMenuForceShowSearch = writable<boolean>(false);

    /*@ngInject*/
    constructor(private definedActionPerformer: DefinedActionPerformer,
                private currentAuthService: CurrentAuthService,
                private currentPageDataService: CurrentPageDataService,
                private loginService: LoginService) {
    }

    public getMenuSettings(): KpMainMenuSettings {
        return window.mainMenu;
    }

    public getMenuItemAnchorHref(menuItem: MenuItem): string {
        if (exists(menuItem.action) && isGoToMenuItemAction(menuItem.action)) {
            return menuItem.action.path;
        }

        return 'javascript:void(0);';
    }

    public async performClick(menuItem: MenuItem): Promise<void> {
        await this.checkUserAuth(menuItem);
        await this.performAction(menuItem.action);
    }

    public setMainMenuForceShowSearch(shown: boolean): void {
        this.mainMenuForceShowSearch.set(shown);
    }

    public getMainMenuForceShowSearch(): Readable<boolean> {
        return this.mainMenuForceShowSearch;
    }

    public getCurrentAuth$(): Observable<Auth> {
        return this.currentAuthService.currentAuth$();
    }

    public async getMenuItems(): Promise<MenuItem[]> {
        try {
            return await this.currentPageDataService.getCurrentPageMenuItems();
        } catch {
            return window.mainMenu.items;
        }
    }

    private async checkUserAuth(menuItem: MenuItem): Promise<void> {
        if (!menuItem.requiresAuth || this.currentAuthService.isLoggedIn()) {
            return;
        }

        await this.loginService.thisPageLogged();
    }

    private async performAction(action: DefinedAction | null): Promise<void> {
        if (!exists(action)) {
            return;
        }

        return this.definedActionPerformer.performAction(action);
    }
}